import * as React from "react";
import { useParams } from "react-router-dom";

import useQuery from "hooks/useQuery";
import useDeferredMutation from "hooks/useDeferredMutation";

import * as S from "@styled";

import IntegrationsBreadcrumbs from "components/integrations/IntegrationsBreadcrumbs";
import ContainerWithLoadingSpinner from "components/shared/ContainerWithLoadingSpinner";
import Text from "components/shared/Text";
import { SaveButton } from "components/shared/Button";
import Input from "components/shared/Input";
import RunDetailsTable from "components/integrations/RunDetailsTable";
import TitleWithArrowLink from "components/shared/TitleWithArrowLink";

import { ReactComponent as HideIcon } from "@icons/hide.svg";

import { Breadcrumb } from "@frends/ui-components/dist/types/breadcrumbs/Breadcrumbs";
import {
    Process,
    ManualTriggerParameter,
    ProcessDetails,
} from "model/process/Process";

import { INTEGRATIONS_BASE_PATH } from "AppRoutes";
import { PROCESSES_APIROUTE } from "./Integrations";
import { ProcessInstanceResponse } from "model/process/ProcessInstance";
import Time from "shared/Time";

const ManualRun: React.FC = () => {
    const { processId } = useParams();

    const apiRoute = `${PROCESSES_APIROUTE}/${processId}`;

    // Track the first instance start time so we can give a start time for subsequent runs
    const [sessionFirstRunStartTime, setSessionFirstRunStartTime] =
        React.useState<string>();

    const processFetch = useQuery<Process>(
        apiRoute,
        undefined,
        undefined,
        "Failed to fetch process.",
    );
    const process = processFetch.data;

    const manualTriggerParametersFetch = useQuery<ProcessDetails>(
        `${apiRoute}/details`,
        undefined,
        undefined,
        "Failed to fetch trigger parameters.",
    );

    const [manualTriggerParameters, setManualTriggerParameters] =
        React.useState<ManualTriggerParameter[]>([]);

    const numberOfRuns = React.useRef(-1);

    React.useEffect(() => {
        if (manualTriggerParametersFetch.data) {
            setManualTriggerParameters(
                manualTriggerParametersFetch.data.processTriggerParams.map(
                    (p) => ({
                        ...p,
                        value: p.defaultValue ?? "",
                    }),
                ),
            );
        }
    }, [manualTriggerParametersFetch.data]);

    const fetchingData =
        processFetch.isLoading || manualTriggerParametersFetch.isLoading;

    const handleParameterValueInputChange = (
        name: string,
        nextValue: string,
    ) => {
        const idx = manualTriggerParameters.findIndex((p) => p.name === name);
        const updated = { ...manualTriggerParameters[idx], value: nextValue };
        const next = [
            ...manualTriggerParameters.slice(0, idx),
            updated,
            ...manualTriggerParameters.slice(idx + 1),
        ];
        setManualTriggerParameters(next);
    };

    const instancesGET = useDeferredMutation<ProcessInstanceResponse>(
        "GET",
        undefined,
        "Failed to fetch available process run instances.",
    );

    const fetchInstances = () => {
        if (!process) {
            return;
        }
        const firstStartTimeStamp = instancesGET.data?.data[0]?.startTimeUtc;
        const startTimestamp =
            sessionFirstRunStartTime ??
            (firstStartTimeStamp &&
                Time.ensureISO8601UtcFormat(firstStartTimeStamp));
        const baseQuery = `processInstance/${process.prodAgentGroupId}?processGuids=${process.processGuid}`;

        const query = sessionFirstRunStartTime
            ? `${baseQuery}&startTimestamp=${startTimestamp}`
            : `${baseQuery}&pageSize=${numberOfRuns.current}`;

        if (!sessionFirstRunStartTime) {
            setSessionFirstRunStartTime(startTimestamp);
        }

        instancesGET.reset();
        instancesGET.mutate({
            url: query,
        });
    };

    const run = useDeferredMutation(
        "PUT",
        fetchInstances,
        "Failed to run process manually.",
    );

    const handleRunIntegrationClick = () => {
        run.reset();
        run.mutate({
            url: `${PROCESSES_APIROUTE}/${process?.productionDeploymentId}/execute`,
            body: manualTriggerParameters.reduce((acc, cur) => {
                acc[cur.name] = cur.value;
                return acc;
            }, {}),
        });
        numberOfRuns.current = numberOfRuns.current + 1;
        fetchInstancesUsingTimeout(500);
    };

    const fetchInstancesUsingTimeout = (timeout: number) => {
        setTimeout(() => {
            fetchInstances();
        }, timeout);
    };

    React.useEffect(() => {
        // Refetch instances until we have something to show
        if (instancesGET.isSuccess && instancesGET.data?.data.length === 0) {
            fetchInstancesUsingTimeout(500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instancesGET.isSuccess]);

    const breadcrumbs: Breadcrumb[] = [
        {
            label: "Run manually",
            href: `${INTEGRATIONS_BASE_PATH}/${processId}/manually-run`,
        },
    ];

    return (
        <S.GridContainer $margin={"1.5rem 0 0 0"}>
            <IntegrationsBreadcrumbs
                breadcrumbs={breadcrumbs}
                organizationId={process?.organizationId}
            />

            <S.ContentContainer>
                <ContainerWithLoadingSpinner loading={fetchingData}>
                    <S.RunManuallyForm>
                        <TitleWithArrowLink
                            title={process?.name ?? ""}
                            href={INTEGRATIONS_BASE_PATH}
                        />

                        {manualTriggerParameters.map((v, i) => (
                            <fieldset key={i}>
                                <Input
                                    label={v.name}
                                    value={v.value}
                                    onChange={(nextValue) =>
                                        handleParameterValueInputChange(
                                            v.name,
                                            String(nextValue),
                                        )
                                    }
                                    disabled={run.isLoading}
                                />

                                {(v.description || v.secret) && (
                                    <p>
                                        {v.description && (
                                            <>
                                                <Text tag="span">
                                                    {v.description}
                                                </Text>
                                                &nbsp;
                                            </>
                                        )}

                                        {v.secret && (
                                            <span title="The parameter will not be logged.">
                                                <HideIcon />
                                            </span>
                                        )}
                                    </p>
                                )}
                            </fieldset>
                        ))}

                        <SaveButton
                            type="button"
                            onClick={handleRunIntegrationClick}
                            disabled={fetchingData}
                            loading={run.isLoading}
                            success={run.isSuccess}
                            error={run.isError}
                        >
                            Run integration
                        </SaveButton>
                    </S.RunManuallyForm>
                </ContainerWithLoadingSpinner>

                <RunDetailsTable
                    processInstances={instancesGET.data?.data}
                    isLoading={
                        instancesGET.data?.data.length === 0 ||
                        instancesGET.isLoading
                    }
                    onRefreshButtonClick={fetchInstances}
                />
            </S.ContentContainer>
        </S.GridContainer>
    );
};

export default ManualRun;
